// frontend/src/components/Layout.tsx
import { ReactNode, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { ThemeToggle } from './ThemeToggle';
import { LogOut, Folders, User, DollarSign, Shield, Menu, X, Star } from 'lucide-react';
import { CreditBalanceBadge } from './CreditBalanceBadge';

interface LayoutProps {
  children: ReactNode;
  showNav?: boolean;
}

export function Layout({ children, showNav = true }: LayoutProps) {
  const { user, signOut, isAdmin, creatorFeaturesEnabled } = useAuth();
  const { tt } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setShowMobileMenu(false);
  }, [navigate]);

  // Redirect openSettings events to /profile
  useEffect(() => {
    const openSettingsHandler = () => {
      navigate('/profile');
    };
    window.addEventListener('openSettings', openSettingsHandler);
    return () => window.removeEventListener('openSettings', openSettingsHandler);
  }, [navigate]);

  // Handle Stripe connect redirect params — forward to /profile
  useEffect(() => {
    const connectParam = searchParams.get('connect');
    if (connectParam === 'success' || connectParam === 'refresh') {
      navigate(`/profile?connect=${connectParam}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-theme-bg bg-mesh">
      {showNav && user && (
        <header className="sticky top-0 z-50 border-b border-theme-border-muted bg-theme-bg/80 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <Link to="/" className="flex items-center gap-2 group">
                <img
                  src="/logo.png"
                  alt="Llambada"
                  className="h-5 w-5 rounded-md shadow-md shadow-accent-500/20"
                />
                <span className="font-display text-lg font-semibold text-theme-text group-hover:text-accent-500 transition-colors hidden sm:inline">
                  Llambada
                </span>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-4">
                {isAdmin && (
                  <Link to="/admin" className="px-2 py-0.5 text-xs rounded bg-theme-primary-muted text-accent-500 font-medium hover:bg-theme-surface-active flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    {tt('Admin')}
                  </Link>
                )}
                <CreditBalanceBadge />
                <Link
                  to="/starred"
                  className="btn btn-ghost text-sm"
                  title={tt('Starred Apps')}
                >
                  <Star className="h-4 w-4" />
                </Link>
                {creatorFeaturesEnabled && (
                  <Link
                    to="/earnings"
                    className="btn btn-ghost text-sm"
                    title={tt('Earnings')}
                  >
                    <DollarSign className="h-4 w-4" />
                  </Link>
                )}
                <ThemeToggle />
                <Link
                  to="/profile"
                  className="btn btn-ghost text-sm"
                  title={tt('Profile & Settings')}
                >
                  <User className="h-4 w-4" />
                </Link>
                <button
                  onClick={handleSignOut}
                  className="btn btn-ghost text-sm"
                  title={tt('Sign out')}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>

              {/* Mobile Menu Button */}
              <div className="flex md:hidden items-center gap-2">
                <CreditBalanceBadge />
                <ThemeToggle />
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="btn btn-ghost p-2"
                  aria-label={tt('Toggle menu')}
                >
                  {showMobileMenu ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {showMobileMenu && (
              <div className="md:hidden border-t border-theme-border-muted py-4 space-y-1 animate-fade-in">
                <Link
                  to="/"
                  className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Folders className="h-4 w-4" />
                  {tt('Browse Public Apps')}
                </Link>

                <Link
                  to="/starred"
                  className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Star className="h-4 w-4" />
                  {tt('Starred Apps')}
                </Link>

                {isAdmin && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <Shield className="h-4 w-4 text-accent-500" />
                    {tt('Admin Dashboard')}
                  </Link>
                )}

                {creatorFeaturesEnabled && (
                  <Link
                    to="/earnings"
                    className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <DollarSign className="h-4 w-4" />
                    {tt('Earnings')}
                  </Link>
                )}

                <Link
                  to="/profile"
                  className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <User className="h-4 w-4" />
                  {tt('Profile & Settings')}
                </Link>

                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left"
                >
                  <LogOut className="h-4 w-4" />
                  {tt('Sign out')}
                </button>
              </div>
            )}
          </div>
        </header>
      )}

      <main>{children}</main>
    </div>
  );
}
